import { useState, useRef, useCallback } from 'react';
import { useSnackbar } from '../../components/common/Snackbar';
import { isValidImportFile } from '../../utils/importUtils';

/**
 * Custom hook for file upload with drag and drop functionality
 * @param {Object} options - Hook options
 * @param {Function} options.onFileSelect - Callback when valid file is selected
 * @param {string} options.errorMessage - Error message to show for invalid files
 * @returns {Object} File upload state and handlers
 */
export const useFileUpload = ({
  onFileSelect,
  errorMessage = 'Invalid file type. Please select a CSV or Excel file.',
} = {}) => {
  const { showSnackbar } = useSnackbar();
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback(
    (file) => {
      if (!file) return;

      if (isValidImportFile(file)) {
        setSelectedFile(file);
        if (onFileSelect) {
          onFileSelect(file);
        }
      } else {
        showSnackbar(errorMessage);
      }
    },
    [onFileSelect, showSnackbar, errorMessage]
  );

  const handleFileInputChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const resetFile = useCallback(() => {
    setSelectedFile(null);
    setDragActive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleDropZoneClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    selectedFile,
    dragActive,
    fileInputRef,
    setSelectedFile,
    handleFileSelect,
    handleFileInputChange,
    handleDrag,
    handleDrop,
    resetFile,
    handleDropZoneClick,
  };
};
